import React, { ReactNode } from 'react';
import { Modal } from 'antd';
import { CheckSquare, Square } from 'react-feather';
import {
  capitalizeFirstLetter,
  Flex,
  Loading,
} from '@mss/mss-component-library';
import { useTranslation } from 'react-i18next';
import Table from 'antd/es/table';
import { TFunction } from 'i18next';
import StatusCircle from './StatusCircle';

type Entry = {
  key: string;
  field: string | ReactNode;
  value: string | number | null | ReactNode;
  children?: Entry[];
};

const countLabel = (label: string | ReactNode, count: number) => (
  <>
    {typeof label === 'string' ? capitalizeFirstLetter(label) : label}{' '}
    <span className="text-sm text-gray-500">({count})</span>
  </>
);

const objectToTree = (obj: any, keyPrefix: string | null = null) => {
  return Object.keys(obj).map((field, i) => {
    const key = keyPrefix ? `${keyPrefix}.${i}` : `${i}`;
    let value = obj[field];
    const entry: Entry = { key, field, value: String(value) };
    if (value instanceof Array) {
      entry.field = countLabel(entry.field, value.length);
      entry.value = null;
      entry.children = value.map((x, ii) => {
        if (x instanceof Object)
          return {
            key: `${i}.${ii}`,
            field: `${typeof x === 'string' ? x : ii}`,
            value: '',
            children: objectToTree(x, `${i}.${ii}`),
          };
        else
          return {
            key: `${i}.${ii}`,
            field: '',
            value: `${x}`,
          };
      });
    } else if (value instanceof Object) {
      entry.field = countLabel(entry.field, Object.keys(value).length);
      entry.value = null;
      entry.children = objectToTree(value, key);
    } else if (typeof value === 'boolean') {
      entry.value = value ? <CheckSquare size={16} /> : <Square size={16} />;
    }
    return entry;
  });
};

const columns = (t: TFunction) => [
  {
    title: t('Field'),
    dataIndex: 'field',
    key: 'field',
    render: (item: any) =>
      typeof item === 'string' ? capitalizeFirstLetter(item) : item,
  },
  {
    title: t('Value'),
    dataIndex: 'value',
    key: 'value',
  },
];
type DetailModalProps = {
  record: any | null;
  closeModal: any;
  parentID?: string;
};

export default function DetailModal({
  record,
  closeModal,
  parentID,
}: DetailModalProps) {
  const [t] = useTranslation(['common']);

  return (
    <Modal
      title={
        <Flex>
          <StatusCircle
            size={8}
            text={{
              data: String(capitalizeFirstLetter(record?.state)),
              hideUnavailable: true,
              italic: true,
            }}
          />
          {parentID && <span className={'ml-2'}>{` > ${parentID}`}</span>}
        </Flex>
      }
      cancelText={t('Close')}
      onCancel={closeModal}
      okButtonProps={{ style: { display: 'none' } }}
      open={!!record}
      width={'50%'}
    >
      {(record && (
        <Table
          columns={columns(t)}
          dataSource={objectToTree(record)}
          expandRowByClick={true}
          showHeader={false}
          pagination={false}
          size="small"
          scroll={{ y: '65vh' }}
        />
      )) || <Loading />}
    </Modal>
  );
}
